import React, { useState, useEffect } from 'react';
import { Card, Row, Col, Typography, Spin, Space, Badge, Table, Input, Empty, Button } from 'antd';
import { TeamOutlined, RightOutlined, SearchOutlined, DownloadOutlined, LeftOutlined } from '@ant-design/icons';
import { baseurl } from '../../../components/lib/settings';
import { exportStandardPDF } from '../../../components/lib/pdfExporter';

const { Title, Text } = Typography;

const AdminMusicianByEdition = () => {
    const [editions, setEditions] = useState([]);
    const [loadingEditions, setLoadingEditions] = useState(true);
    const [selectedDelegation, setSelectedDelegation] = useState(null);
    const [selectedEdition, setSelectedEdition] = useState(null);
    const [musicians, setMusicians] = useState([]);
    const [loadingMusicians, setLoadingMusicians] = useState(false);
    const [searchText, setSearchText] = useState('');

    useEffect(() => {
        fetchEditions();
    }, []);

    const fetchEditions = async () => {
        try {
            const response = await baseurl.get('v3/ngoma/manage_editions/');
            setEditions(response.data);
        } catch (error) {
            console.error("Erro ao carregar edições", error);
        } finally {
            setLoadingEditions(false);
        }
    };

    // Extract unique delegations from editions
    const delegations = Array.from(new Set(editions.map(e => e.delegacao_desc)))
        .map(desc => {
            return editions.find(e => e.delegacao_desc === desc);
        })
        .filter(Boolean);

    // Filter editions by selected delegation
    const delegationEditions = selectedDelegation 
        ? editions.filter(e => e.delegacao_id === selectedDelegation.delegacao_id)
        : [];

    const handleSelectDelegation = (delegation) => {
        setSelectedDelegation(delegation);
        setSelectedEdition(null); // Reset edition when picking new delegation
        setMusicians([]);
    };

    const handleSelectEdition = async (edition) => {
        setSelectedEdition(edition);
        setLoadingMusicians(true);
        try {
            const response = await baseurl.post('v3/ngoma/administrator/musician/list_musicians/', {
                edicao_id: edition.id
            });
            setMusicians(response.data);
        } catch (error) {
            console.error("Erro ao carregar músicos", error);
        } finally {
            setLoadingMusicians(false);
        }
    };

    const exportToPDF = async () => {
        setLoadingMusicians(true);
        try {
            const tableColumn = ["#", "Código", "Músico / Autor", "Título", "Contacto", "Nr. WhatsApp", "Estado"];
            const tableRows = filteredMusicians.map((musician, index) => [
                index + 1,
                musician.voting_code || '-',
                musician.author || '-',
                musician.title || '-',
                musician.contact || '-',
                musician.whatsapp_number || '-',
                musician.available_to_vote ? 'ACTIVO' : 'INATIVO'
            ]);

            const baseUrlStr = baseurl.defaults.baseURL.replace(/\/api\/?$/, '');
            await exportStandardPDF({
                docTitle: `Lista de Músicos - ${selectedEdition?.delegacao_desc} (${selectedEdition?.description})`,
                tableColumn,
                tableRows,
                delegacaoLogoUrl: selectedEdition?.delegacao_logo ? baseUrlStr + selectedEdition.delegacao_logo : null,
                fileName: `musicos_${selectedEdition?.delegacao_desc}_${selectedEdition?.description}.pdf`
            });
        } finally {
            setLoadingMusicians(false);
        }
    };

    const columns = [
        {
            title: 'Código',
            dataIndex: 'voting_code',
            key: 'voting_code',
            render: (text) => <Text strong>{text}</Text>,
            width: '100px'
        },
        {
            title: 'Músico / Autor',
            dataIndex: 'author',
            key: 'author',
        },
        {
            title: 'Título',
            dataIndex: 'title',
            key: 'title',
        },
        {
            title: 'Estado',
            dataIndex: 'available_to_vote',
            key: 'available_to_vote',
            render: (available) => (
                <Badge 
                    status={available ? 'success' : 'error'} 
                    text={available ? 'Activo' : 'Inativo'} 
                />
            )
        }
    ];

    const filteredMusicians = musicians.filter(m => 
        (m.author && m.author.toLowerCase().includes(searchText.toLowerCase())) ||
        (m.title && m.title.toLowerCase().includes(searchText.toLowerCase())) ||
        (m.voting_code && m.voting_code.toLowerCase().includes(searchText.toLowerCase()))
    );

    return (
        <div style={{ padding: '24px', minHeight: '80vh' }}>
            <div style={{ marginBottom: '24px' }}>
                <Space>
                    <TeamOutlined style={{ fontSize: '28px', color: '#1890ff' }} />
                    <Title level={3} style={{ margin: 0, fontFamily: 'Montserrat, sans-serif' }}>
                        Lista de Músicos por Edição
                    </Title>
                </Space>
                <p style={{ color: '#666', marginTop: '8px' }}>
                    Selecione uma delegação e edição à esquerda para visualizar e exportar todos os músicos concorrentes associados.
                </p>
            </div>

            <Row gutter={[24, 24]}>
                {/* COLUNA ESQUERDA - LISTA DE DELEGAÇÕES / EDIÇÕES */}
                <Col xs={24} lg={selectedEdition ? 8 : 24} style={{ transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)' }}>
                    <Card
                        title={
                            <Space>
                                {selectedDelegation && (
                                    <Button 
                                        type="text" 
                                        icon={<LeftOutlined />} 
                                        onClick={() => {
                                            setSelectedDelegation(null);
                                            setSelectedEdition(null);
                                        }}
                                        style={{ marginLeft: '-12px' }}
                                    />
                                )}
                                <Text strong>{selectedDelegation ? `Edições: ${selectedDelegation.delegacao_desc}` : 'Selecione a Delegação'}</Text>
                            </Space>
                        }
                        style={{ 
                            borderRadius: '16px', 
                            boxShadow: '0 4px 24px rgba(0,0,0,0.04)',
                            border: 'none',
                            height: '100%'
                        }}
                        bodyStyle={{ padding: '16px', maxHeight: '70vh', overflowY: 'auto' }}
                    >
                        {loadingEditions ? (
                            <div style={{ textAlign: 'center', padding: '40px' }}>
                                <Spin size="large" />
                            </div>
                        ) : !selectedDelegation ? (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                                {delegations.map(del => (
                                    <Card
                                        key={del.delegacao_id}
                                        hoverable
                                        onClick={() => handleSelectDelegation(del)}
                                        style={{
                                            borderRadius: '12px',
                                            border: '1px solid #f0f0f0',
                                            transition: 'all 0.3s ease',
                                        }}
                                        bodyStyle={{ padding: '16px' }}
                                    >
                                        <Row align="middle" justify="space-between">
                                            <Col span={20}>
                                                <Text strong style={{ fontSize: '15px' }}>
                                                    {del.delegacao_desc}
                                                </Text>
                                            </Col>
                                            <Col span={4} style={{ textAlign: 'right' }}>
                                                <RightOutlined style={{ color: '#d9d9d9' }} />
                                            </Col>
                                        </Row>
                                    </Card>
                                ))}
                            </div>
                        ) : (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', animation: 'fadeInRight 0.3s' }}>
                                {delegationEditions.length === 0 ? (
                                    <Empty description="Nenhuma edição encontrada para esta delegação." />
                                ) : (
                                    delegationEditions.map(edition => (
                                        <Card
                                            key={edition.id}
                                            hoverable
                                            onClick={() => handleSelectEdition(edition)}
                                            style={{
                                                borderRadius: '12px',
                                                border: selectedEdition?.id === edition.id ? '2px solid #1890ff' : '1px solid #f0f0f0',
                                                boxShadow: selectedEdition?.id === edition.id ? '0 4px 12px rgba(24, 144, 255, 0.15)' : 'none',
                                                transition: 'all 0.3s ease',
                                                background: selectedEdition?.id === edition.id ? '#f0f7ff' : '#fff'
                                            }}
                                            bodyStyle={{ padding: '16px' }}
                                        >
                                            <Row align="middle" justify="space-between">
                                                <Col span={20}>
                                                    <Space direction="vertical" size={0}>
                                                        <Text strong style={{ fontSize: '15px' }}>
                                                            {edition.description}
                                                        </Text>
                                                        <Badge 
                                                            status={edition.is_active ? 'success' : 'default'} 
                                                            text={<Text type="secondary" style={{ fontSize: '12px' }}>{edition.is_active ? 'Ativa' : 'Fechada'}</Text>} 
                                                            style={{ marginTop: '4px' }}
                                                        />
                                                    </Space>
                                                </Col>
                                                <Col span={4} style={{ textAlign: 'right' }}>
                                                    <RightOutlined style={{ color: selectedEdition?.id === edition.id ? '#1890ff' : '#d9d9d9' }} />
                                                </Col>
                                            </Row>
                                        </Card>
                                    ))
                                )}
                            </div>
                        )}
                    </Card>
                </Col>

                {/* COLUNA DIREITA - LISTA DE MÚSICOS E PDF (SLIDE IN) */}
                {selectedEdition && (
                    <Col xs={24} lg={16} style={{ animation: 'fadeInRight 0.5s cubic-bezier(0.4, 0, 0.2, 1)' }}>
                        <Card
                            title={
                                <Row justify="space-between" align="middle">
                                    <Col>
                                        <Space direction="vertical" size={0}>
                                            <Text type="secondary" style={{ fontSize: '12px' }}>Músicos Registados</Text>
                                            <Text strong style={{ fontSize: '18px' }}>{selectedEdition.delegacao_desc} — {selectedEdition.description}</Text>
                                        </Space>
                                    </Col>
                                    <Col>
                                        <Button 
                                            type="primary" 
                                            icon={<DownloadOutlined />} 
                                            onClick={exportToPDF}
                                            disabled={musicians.length === 0}
                                            style={{ borderRadius: '8px' }}
                                        >
                                            Exportar PDF
                                        </Button>
                                    </Col>
                                </Row>
                            }
                            style={{ 
                                borderRadius: '16px', 
                                boxShadow: '0 12px 32px rgba(0,0,0,0.08)',
                                border: 'none',
                                height: '100%'
                            }}
                            bodyStyle={{ padding: '24px' }}
                        >
                            <Input
                                placeholder="Pesquisar por autor, título ou código..."
                                prefix={<SearchOutlined style={{ color: '#bfbfbf' }} />}
                                onChange={e => setSearchText(e.target.value)}
                                style={{ marginBottom: '24px', borderRadius: '8px', padding: '8px 12px' }}
                                size="large"
                            />
                            
                            {loadingMusicians ? (
                                <div style={{ textAlign: 'center', padding: '60px' }}>
                                    <Spin size="large" />
                                </div>
                            ) : musicians.length === 0 ? (
                                <Empty description="Nenhum músico encontrado para esta edição." />
                            ) : (
                                <Table 
                                    dataSource={filteredMusicians} 
                                    columns={columns} 
                                    rowKey="id"
                                    pagination={{ pageSize: 8, showSizeChanger: false }}
                                    style={{ border: '1px solid #f0f0f0', borderRadius: '8px', overflow: 'hidden' }}
                                />
                            )}
                        </Card>
                    </Col>
                )}
            </Row>

            <style>{`
                @keyframes fadeInRight {
                    from { opacity: 0; transform: translateX(40px); }
                    to { opacity: 1; transform: translateX(0); }
                }
            `}</style>
        </div>
    );
};

export default AdminMusicianByEdition;
